import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { PaginationSizeSelector } from "common/tableComponents/pagination/paginationSizeSelector";
import { debounce } from "lodash";
import { ServerSideDataGrid } from "../../../../common/components/serverSideDataGrid/serverSideDataGrid";
import BillingApproval from "../../../../redux/models/billingApproval";
import K from "../../../../utilities/constants";
import { selectUserId } from "../../../../redux/slices/userSlice";
import { jobBilling } from "../tableColumns";
import {
  calculateTableHeight,
  onFilterChanged,
  haveVbdWarning,
  differentCurrencyWarning,
  changeTableHeight,
} from "./billingComponentsHelper";
import AgGridUtility from "../../../../utilities/agGridUtility";

export const JobBillingsTable = ({
  clientId,
  clientName,
  pageStates,
  setPageStates,
  setRowData,
  setTotalLoading,
  onBillingSelected,
  gridRef,
  setIsExportDisabled,
}) => {
  const userId = useSelector(selectUserId);

  const [height, setHeight] = useState(310);
  const [availableIds, setAvailableIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const tableColumns = useMemo(() => jobBilling, []);

  useEffect(() => {
    BillingApproval.getJobSubmittedBillingIds(clientId).then((r) =>
      setAvailableIds(r.data),
    );
  }, []);

  const getJobServerSideData = useCallback(
    (options) => {
      return BillingApproval.getJobSubmittedBillings(clientId, {
        ...options,
      });
    },
    [clientId],
  );

  useEffect(() => {
    if (pageStates.jobLoading) {
      gridRef?.current?.api?.deselectAll();
      gridRef?.current?.api?.refreshServerSide();
    }
    setPageStates((prev) => ({ ...prev, jobLoading: false }));
  }, [pageStates.jobLoading]);

  const onDataFetchFailed = useCallback((err) => console.error(err), []);

  const onDataFetchSuccess = useCallback(
    (data) => {
      setIsExportDisabled(data.rowCount <= 0);
      setHeight(calculateTableHeight(data.rowData));
    },
    [setIsExportDisabled],
  );

  const onRowSelected = () => {
    setTotalLoading((prev) => ({ ...prev, job: true }));
  };

  const onSelectionChanged = debounce(async (event) => {
    await updateSelection(event.api);
  }, 1000);

  const updateSelection = async (gridApi) => {
    const selectedRows = gridApi.getServerSideSelectionState();
    const updatedSelectedRows = AgGridUtility.getFilteredSelectedRows(
      gridApi,
      availableIds,
    );
    setSelectedIds(updatedSelectedRows);
    const filterModel = gridApi.getFilterModel();
    const isFilterApplied = filterModel?.isManual?.values.length > 0;
    const res = await BillingApproval.getJobBillingsTotal(clientId, {
      selectAll: isFilterApplied ? false : selectedRows.selectAll,
      toggledNodes: isFilterApplied
        ? updatedSelectedRows.map(String)
        : selectedRows.toggledNodes,
    });
    setTotalLoading((prev) => ({ ...prev, job: false }));

    const {
      total,
      isHaveVbd,
      isSameType,
      missedOfferSalaryBillings,
      totalCount,
    } = res.data;

    setPageStates((prev) => ({
      ...prev,
      checkedRows: {
        ...pageStates.checkedRows,
        jobTotal: total,
        jobSelectAll: isFilterApplied ? false : selectedRows.selectAll,
        jobSelectedNodes: isFilterApplied
          ? updatedSelectedRows.map(String)
          : selectedRows.toggledNodes,
        offerSalaryMissingRows: missedOfferSalaryBillings.length,
        isSameTypeJobs: totalCount === 0 || !isSameType,
      },
    }));
    setRowData(missedOfferSalaryBillings);

    if (!isSameType) differentCurrencyWarning();
    if (isHaveVbd) haveVbdWarning();
  };

  return (
    <ServerSideDataGrid
      minHeight={310}
      height={height}
      ref={gridRef}
      userId={userId}
      configKey={K.AgGridTable.Keys.SubmittedJobBillingsVisibleColumn}
      getData={getJobServerSideData}
      defaultColumnConfig={tableColumns}
      defaultColDef={K.AgGridTable.DefaultColDef}
      onDataFetchFailed={onDataFetchFailed}
      pagination
      paginationPageSize={25}
      cacheBlockSize={25}
      rowSelection="multiple"
      suppressRowClickSelection
      onFilterChanged={(event) =>
        onFilterChanged(event.api, setHeight, selectedIds, updateSelection)
      }
      onPaginationChanged={(event) => changeTableHeight(event.api, setHeight)}
      onDataFetchSuccess={onDataFetchSuccess}
      onSelectionChanged={onSelectionChanged}
      onRowSelected={onRowSelected}
      rowStyle={{ cursor: "pointer" }}
      onRowClicked={(event) => {
        onBillingSelected({
          type: K.BillingType.Job,
          details: event.data,
          gridApi: gridRef.current?.api,
          clientName,
        });
      }}
      components={{
        PaginationSizeSelector: (props) => (
          <PaginationSizeSelector
            {...props}
            gridRef={gridRef}
            getData={getJobServerSideData}
          />
        ),
      }}
      statusBar={{
        statusPanels: [
          {
            statusPanel: "PaginationSizeSelector",
            align: "left",
          },
        ],
      }}
    />
  );
};
